<!-- eslint-disable -->
<template>
  <div class="cp-panel">
    <!-- No client linked -->
    <div v-if="!clientId" class="cp-empty">
      <feather-icon icon="UserXIcon" size="26" class="text-muted mb-1" />
      <p class="text-muted mb-0">
        This opportunity is not linked to an ERP client yet.
      </p>
      <small class="text-muted">Once the client exists in the system, their full record shows here.</small>
    </div>

    <div v-else-if="loading" class="text-center py-3">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="sr-only">Loading…</span>
      </div>
    </div>

    <div v-else-if="error" class="cp-empty">
      <feather-icon icon="AlertCircleIcon" size="24" class="text-danger mb-1" />
      <p class="text-muted mb-0">Could not load this client's details.</p>
    </div>

    <div v-else-if="client">
      <!-- Client identity -->
      <div class="cp-head">
        <div class="cp-avatar">{{ initials(client.name) }}</div>
        <div class="cp-head__info">
          <div class="cp-head__name">{{ client.name || 'Client' }}</div>
          <div class="cp-head__company">
            <feather-icon icon="BriefcaseIcon" size="11" class="mr-05" />
            {{ company.name || '—' }}
          </div>
        </div>
      </div>

      <!-- Contact + company facts -->
      <dl class="cp-dl">
        <div><dt>Email</dt><dd>{{ client.email || '—' }}</dd></div>
        <div><dt>Phone</dt><dd>{{ client.phone || '—' }}</dd></div>
        <div><dt>CR number</dt><dd>{{ company.CR_num || '—' }}</dd></div>
        <div><dt>CR expiry</dt><dd :class="crExpired ? 'text-danger font-weight-bold' : ''">{{ company.CR_end_date || '—' }}</dd></div>
        <div><dt>Activity</dt><dd>{{ company.activity || '—' }}</dd></div>
        <div><dt>Address</dt><dd>{{ company.address || '—' }}</dd></div>
      </dl>

      <!-- Products this client generated with us (CRM product <-> ERP certificate) -->
      <h6 class="cp-section">
        Products generated
        <span class="cp-count">{{ productRows.length }}</span>
      </h6>

      <div v-if="loadingProducts" class="text-muted small py-1">Loading products…</div>
      <div v-else-if="!productRows.length" class="text-muted small py-1">
        No products recorded for this client yet.
      </div>
      <div v-else class="cp-products">
        <article v-for="p in visibleProductRows" :key="p.erpCertificateId || p.crmName" class="cp-product">
          <div class="d-flex justify-content-between align-items-start">
            <!-- crmName is null when this certificate has no curated crm_products
                 catalog entry — fall back to its real ERP name rather than
                 showing nothing (see CrmProductController::clientProducts). -->
            <span class="cp-product__name" :title="p.regulation">{{ p.crmName || p.certificateName || 'Certificate' }}</span>
            <span class="cp-product__qty">&times;{{ p.count }}</span>
          </div>
          <div class="cp-product__meta">{{ p.regulation || p.certificateName || '—' }}</div>
          <div class="cp-product__dates">
            <span>First {{ p.firstIssued || '—' }}</span>
            <span>Last {{ p.lastIssued || '—' }}</span>
          </div>
        </article>

        <button
          v-if="productRows.length > productLimit"
          type="button"
          class="cp-more"
          @click="showAllProducts = !showAllProducts"
        >
          {{ showAllProducts ? 'Show less' : `Show all ${productRows.length}` }}
        </button>
      </div>

      <!-- Certificates already issued -->
      <h6 class="cp-section">
        Certificates with us
        <span class="cp-count">{{ certificates.length }}</span>
      </h6>

      <div v-if="!certificates.length" class="text-muted small py-1">
        No certificates recorded yet.
      </div>

      <div v-else class="cp-certs">
        <article v-for="cert in visibleCerts" :key="cert.id" class="cp-cert">
          <div class="d-flex justify-content-between align-items-start">
            <span class="cp-cert__name">{{ cert.certificate_name || subName(cert) || 'Certificate' }}</span>
            <span
              class="cp-chip"
              :class="expiryClass(cert.expired_at)"
              :title="`Expires ${cert.expired_at || 'unknown'}`"
            >
              {{ expiryLabel(cert.expired_at) }}
            </span>
          </div>
          <div class="cp-cert__meta">
            <span v-if="cert.certificate_number">#{{ cert.certificate_number }}</span>
            <span v-if="cert.request_code">· {{ cert.request_code }}</span>
            <span v-if="cert.source">· {{ cert.source }}</span>
          </div>
          <div class="cp-cert__dates">
            <span>Issued {{ cert.issued_at || '—' }}</span>
            <span>Expires {{ cert.expired_at || '—' }}</span>
          </div>
        </article>

        <button
          v-if="certificates.length > certLimit"
          type="button"
          class="cp-more"
          @click="showAllCerts = !showAllCerts"
        >
          {{ showAllCerts ? 'Show less' : `Show all ${certificates.length}` }}
        </button>
      </div>

      <!-- Other opportunities for this client -->
      <h6 class="cp-section">
        Other opportunities
        <span class="cp-count">{{ otherCards.length }}</span>
      </h6>
      <div v-if="!otherCards.length" class="text-muted small py-1">
        This is the only opportunity for this client.
      </div>
      <div v-else class="cp-opps">
        <div v-for="c in otherCards" :key="c.id" class="cp-opp">
          <span class="cp-opp__pipe" :class="`cp-opp__pipe--${c.pipeline}`">{{ pipeLabel(c.pipeline) }}</span>
          <span class="cp-opp__title">{{ c.service_scheme || c.title }}</span>
          <span class="cp-opp__stage">{{ c.stage }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'ClientPanel',
  props: {
    card: { type: Object, required: true }
  },
  data() {
    return {
      loading: false,
      error: false,
      client: null,
      certLimit: 4,
      showAllCerts: false,
      productRows: [],
      loadingProducts: false,
      // Karim, 2 Sep: "the product, you are giving it in a table, but there is
      // also a lot" — same treatment as the certificates list just above.
      productLimit: 4,
      showAllProducts: false
    }
  },
  computed: {
    clientId() {
      return this.card ? (this.card.clientId || this.card.client_id) : null
    },
    company() {
      return (this.client && this.client.company) || {}
    },
    certificates() {
      return (this.client && this.client.certificates) || []
    },
    visibleCerts() {
      return this.showAllCerts ? this.certificates : this.certificates.slice(0, this.certLimit)
    },
    visibleProductRows() {
      return this.showAllProducts ? this.productRows : this.productRows.slice(0, this.productLimit)
    },
    otherCards() {
      const all = (this.client && this.client.cards) || []
      return all.filter(c => c.id !== this.card.id)
    },
    crExpired() {
      if (!this.company.CR_end_date) return false
      return new Date(this.company.CR_end_date) < new Date()
    }
  },
  watch: {
    clientId: { handler: 'fetchClient', immediate: true }
  },
  methods: {
    initials(name) {
      if (!name) return '?'
      return name.trim().split(/\s+/).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('')
    },
    subName(cert) {
      const n = cert.sub_certificate_name
      if (!n) return null
      if (typeof n === 'string') return n
      return n.en || n.ar || null
    },
    pipeLabel(p) {
      const map = { renewals: 'Renewal', deals: 'New Deal', tickets: 'Ticket' }
      return map[p] || p
    },
    daysTo(date) {
      if (!date) return null
      const diff = new Date(date) - new Date()
      return Math.round(diff / 86400000)
    },
    expiryLabel(date) {
      const d = this.daysTo(date)
      if (d === null) return 'No date'
      if (d < 0) return `Expired ${Math.abs(d)}d ago`
      if (d === 0) return 'Expires today'
      return `${d}d left`
    },
    expiryClass(date) {
      const d = this.daysTo(date)
      if (d === null) return 'cp-chip--muted'
      if (d < 0) return 'cp-chip--danger'
      if (d <= 60) return 'cp-chip--warning'
      return 'cp-chip--success'
    },

    async fetchProducts() {
      if (!this.clientId) return
      this.loadingProducts = true
      try {
        const res = await axios.get(`crm/clients/${this.clientId}/products`)
        const body = decryptData(res.data.payload)
        this.productRows = (body && body.data && body.data.products) || []
      } catch (e) {
        this.productRows = []
      } finally {
        this.loadingProducts = false
      }
    },

    async fetchClient() {
      if (!this.clientId) return
      this.fetchProducts()
      this.loading = true
      this.error = false
      try {
        const res = await axios.get(`crm/clients/${this.clientId}`)
        const body = decryptData(res.data.payload)
        this.client = (body && body.data) || null
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cp-panel { font-size: .8rem; }
.cp-empty { text-align: center; padding: 1.2rem .5rem; }

.cp-head { display: flex; align-items: center; margin-bottom: .6rem; }
.cp-avatar {
  width: 38px; height: 38px; border-radius: 50%; margin-right: .55rem; flex-shrink: 0;
  background: linear-gradient(135deg, #2563eb, #9e95f5); color: #fff;
  display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: .82rem;
}
.cp-head__name { font-weight: 700; font-size: .9rem; line-height: 1.1; }
.cp-head__company { font-size: .74rem; color: #6e6b7b; display: flex; align-items: center; margin-top: .1rem; }

.cp-dl {
  margin: 0 0 .5rem;
  > div { display: flex; justify-content: space-between; padding: .28rem 0; border-bottom: 1px dashed #f2f2f7; }
  dt { font-size: .72rem; color: #6e6b7b; font-weight: 600; margin: 0; }
  dd { font-size: .78rem; margin: 0; text-align: right; max-width: 62%; word-break: break-word; }
}

.cp-section {
  display: flex; align-items: center; justify-content: space-between;
  font-size: .72rem; text-transform: uppercase; color: #6e6b7b; font-weight: 700;
  margin: .9rem 0 .4rem;
}
.cp-count { background: #ededf3; color: #6e6b7b; border-radius: 10px; padding: 0 .4rem; font-size: .68rem; }

.cp-products { display: flex; flex-direction: column; gap: .3rem; margin-bottom: .2rem; }
.cp-product {
  border: 1px solid #ece9fd; border-radius: .35rem; padding: .35rem .5rem; background: #f9f8ff;
  &__name { font-weight: 700; font-size: .77rem; color: #6b4ed8; line-height: 1.2; }
  &__qty {
    font-size: .66rem; font-weight: 700; color: #2563eb;
    background: rgba(37, 99, 235,.14); border-radius: 10px; padding: .05rem .38rem; white-space: nowrap;
  }
  &__meta { font-size: .68rem; color: #6e6b7b; margin-top: .1rem; }
  &__dates { display: flex; justify-content: space-between; font-size: .66rem; color: #a0a0ad; margin-top: .12rem; }
}

.cp-certs { display: flex; flex-direction: column; gap: .35rem; }
.cp-cert {
  border: 1px solid #f0f0f5; border-radius: .35rem; padding: .4rem .5rem; background: #fbfbfd;
  &__name { font-weight: 600; font-size: .77rem; line-height: 1.2; }
  &__meta { font-size: .68rem; color: #6e6b7b; margin-top: .12rem; }
  &__dates { display: flex; justify-content: space-between; font-size: .68rem; color: #a0a0ad; margin-top: .15rem; }
}

.cp-chip {
  font-size: .63rem; font-weight: 700; padding: .05rem .38rem; border-radius: 10px; white-space: nowrap; margin-left: .3rem;
  &--danger { background: rgba(234,84,85,.14); color: #ea5455; }
  &--warning { background: rgba(59, 130, 246,.16); color: #3b82f6; }
  &--success { background: rgba(40,199,111,.14); color: #28c76f; }
  &--muted { background: #f1f1f6; color: #a0a0ad; }
}

.cp-more {
  border: 0; background: transparent; color: #2563eb; font-size: .72rem; font-weight: 600;
  cursor: pointer; padding: .2rem 0; text-align: left;
}

.cp-opps { display: flex; flex-direction: column; gap: .25rem; }
.cp-opp {
  display: flex; align-items: center; gap: .35rem; font-size: .74rem;
  border: 1px solid #f0f0f5; border-radius: .3rem; padding: .25rem .4rem;
  &__pipe {
    font-size: .62rem; font-weight: 700; padding: .05rem .35rem; border-radius: 8px;
    &--renewals { background: rgba(59, 130, 246,.16); color: #3b82f6; }
    &--deals { background: rgba(37, 99, 235,.14); color: #2563eb; }
    &--tickets { background: rgba(0,207,232,.14); color: #00a5b5; }
  }
  &__title { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__stage { font-size: .68rem; color: #6e6b7b; text-transform: capitalize; }
}

.mr-05 { margin-right: .25rem; }
</style>
